import { Flex, IconButton, Link, Text, Tooltip } from '@chakra-ui/react';
import { EditIcon } from '@udacity/chakra-uds-icons';
import { DataTable } from 'app/common/components/uds-table';
import { DataTableColumn, PaginationData } from 'app/common/components/uds-table/types';
import { dateFormatter } from 'app/helpers/format-helper';
import { FetchedMeeting } from 'app/uconnect/helpers/types';
import { getAttendedCount, hasManualEdits } from 'app/uconnect/helpers/utils';
import { AttendanceProvenanceBadge } from 'app/uconnect/meeting/attendance-provenance-badge';
import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Link as RouterLink } from 'react-router-dom';

type Props = {
  meetings: FetchedMeeting[];
};

const pastSessionsPaginationData: PaginationData = {
  defaultPageSize: 50,
};

export const PastSessionsList: React.FC<Props> = ({ meetings }) => {
  const { t } = useTranslation();

  const sortedMeetings = useMemo(
    () => orderBy(meetings, (meeting) => Date.parse(meeting.startAt), ['desc']),
    [meetings]
  );

  return (
    <DataTable
      id="past-sessions"
      columns={getPastSessionsTableColumns(t)}
      data={sortedMeetings}
      paginationData={pastSessionsPaginationData}
    />
  );
};

function getPastSessionsTableColumns(t: TFunction): DataTableColumn<FetchedMeeting>[] {
  return [
    {
      id: 'session',
      title: t('connect.sessionsHeader'),
      getRow: (meeting) => {
        const date = dateFormatter.short(meeting.startAt);
        const time = dateFormatter.localTime(meeting.startAt);
        return {
          data: `${meeting.name} ${date} ${time}`,
          sortValue: Date.parse(meeting.startAt),
          component: (
            <Link
              as={RouterLink}
              to={`/connect/meetings/${meeting.id}`}
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
            >
              <Text fontWeight={600}>{meeting.name}</Text>
              <Text color="gray.600" fontSize="sm">
                {date} {time}
              </Text>
            </Link>
          ),
        };
      },
    },
    {
      id: 'attended',
      title: t('connect.attendance'),
      disableSorting: true,
      getRow: (meeting) => {
        return {
          data: t('connect.attendedSummary', {
            attended: getAttendedCount(meeting),
            total: meeting.meetingUsers.totalCount,
          }),
        };
      },
    },
    {
      id: 'edited',
      title: '',
      disableSorting: true,
      getRow: (meeting) => {
        const edited = hasManualEdits(meeting);
        return {
          data: edited ? t('connect.edited') : '',
          component: <Flex>{edited && <AttendanceProvenanceBadge edited />}</Flex>,
        };
      },
    },
    {
      id: 'edit',
      title: '',
      disableSorting: true,
      width: '4rem',
      getRow: (meeting) => {
        return {
          data: '',
          component: (
            <Flex justifyContent="flex-end">
              <Tooltip label={t('connect.editAttendance')}>
                <IconButton
                  as={RouterLink}
                  to={`/connect/meetings/${meeting.id}`}
                  aria-label={t('connect.editAttendance')}
                  icon={<EditIcon h={5} w={5} />}
                  variant="ghost"
                  size="sm"
                />
              </Tooltip>
            </Flex>
          ),
        };
      },
    },
  ];
}
